import axiosInstance from "../components/pages/ApiFunction/axiosInstance";

/** Normalize any document/image field into a usable URL string */
export const getDocUrl = (val) => {
  if (!val) return "";
  if (typeof val === "string") {
    let trimmed = val.trim().replace(/^["']|["']$/g, "");
    if (!trimmed || trimmed === "null" || trimmed === "undefined") return "";

    // Comma-separated front/back (or multi) URLs — take first valid http(s) part
    if (trimmed.includes(",") && /https?:\/\//i.test(trimmed)) {
      const parts = trimmed
        .split(",")
        .map((s) => s.trim().replace(/^["']|["']$/g, ""))
        .filter((s) => /^https?:\/\//i.test(s) || s.startsWith("/") || s.startsWith("blob:") || s.startsWith("data:"));
      if (parts.length) trimmed = parts[0];
    }

    // Protocol-relative CDN URLs
    if (trimmed.startsWith("//")) {
      trimmed = `https:${trimmed}`;
    }

    // Relative uploads path → absolute API host (skip CRA bundled assets)
    if (
      trimmed.startsWith("/") &&
      !trimmed.startsWith("//") &&
      !trimmed.startsWith("/static/") &&
      typeof window !== "undefined"
    ) {
      try {
        const apiBase =
          (typeof process !== "undefined" &&
            process.env?.REACT_APP_BASEURL) ||
          "https://api.binsapp.ai/";
        trimmed = new URL(trimmed.replace(/^\//, ""), apiBase).href;
      } catch {
        /* keep relative */
      }
    }

    return trimmed;
  }
  if (Array.isArray(val)) {
    for (const item of val) {
      const u = getDocUrl(item);
      if (u) return u;
    }
    return "";
  }
  if (typeof val === "object") {
    return getDocUrl(
      val.url ||
        val.fileUrl ||
        val.location ||
        val.path ||
        val.secure_url ||
        val.src ||
        val.link ||
        val.uri ||
        val.file ||
        val.key ||
        ""
    );
  }
  return String(val || "").trim();
};

export const getDocUrls = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) {
    return val.map((item) => getDocUrl(item)).filter(Boolean);
  }
  if (typeof val === "string" && val.includes(",") && /https?:\/\//i.test(val)) {
    return val
      .split(",")
      .map((s) => s.trim())
      .filter((s) => /^https?:\/\//i.test(s) || s.startsWith("/") || s.startsWith("blob:"));
  }
  const single = getDocUrl(val);
  return single ? [single] : [];
};

const pathWithoutQuery = (url) => {
  if (!url || typeof url !== "string") return "";
  try {
    return url.split("?")[0].split("#")[0].toLowerCase();
  } catch {
    return url.toLowerCase();
  }
};

export const isPdfUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const path = pathWithoutQuery(url);
  const lower = url.toLowerCase();
  return (
    path.endsWith(".pdf") ||
    lower.includes("application/pdf") ||
    lower.includes("contenttype=application%2fpdf") ||
    lower.includes("content-type=application/pdf")
  );
};

export const isHeicUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const path = pathWithoutQuery(url);
  const lower = url.toLowerCase();
  return (
    /\.(heic|heif)$/i.test(path) ||
    lower.includes("image/heic") ||
    lower.includes("image/heif") ||
    lower.includes("contenttype=image%2fheic") ||
    lower.includes("contenttype=image%2fheif")
  );
};

/** True for common image extensions */
export const isImageUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const path = pathWithoutQuery(url);
  return /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif|avif|tiff?|ico|jfif)$/i.test(
    path
  );
};

/**
 * Should we try rendering as an image in the modal?
 * Prefer explicit image extensions; otherwise anything that isn't clearly a PDF/non-image.
 */
export const shouldPreviewAsImage = (url) => {
  if (!url) return false;
  if (isPdfUrl(url)) return false;
  if (isImageUrl(url)) return true;
  const path = pathWithoutQuery(url);
  const knownNonImage =
    /\.(pdf|doc|docx|xls|xlsx|csv|zip|rar|7z|mp4|mov|avi|mp3|wav|txt)$/i.test(
      path
    );
  return !knownNonImage;
};

/** Same-origin API proxy that converts HEIC→JPEG server-side (bypasses GCS CORS). */
export const getPreviewProxyUrl = (fileUrl) => {
  const clean = getDocUrl(fileUrl);
  if (!clean) return "";
  const apiBase = axiosInstance.defaults.baseURL;
  const base = apiBase.endsWith("/") ? apiBase : `${apiBase}/`;
  return `${base}api/preview-file?url=${encodeURIComponent(clean)}`;
};
